import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, TrendingUp, Users, Building2, Target, Heart, Briefcase, TrendingUp as Growth, FileText, CheckSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import siteConfig from '../config/siteContent';

const HomePage = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stakeholders = [
    {
      title: 'Owners',
      description: 'We support owners in the investment journey through Partnership and Performance. Our disciplined approach to asset management ensures sustainable growth and optimal returns.',
      image: 'https://images.unsplash.com/photo-1711743266323-5badf42d4797',
      icon: Building2
    },
    {
      title: 'Guests',
      description: 'Our dedication to exceptional service is unwavering. We create memorable experiences through 5-star service, attention to detail, and genuine hospitality.',
      image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa',
      icon: Heart
    },
    {
      title: 'Team',
      description: 'We have created the home of hospitality where the best and brightest talent can come together and flourish. Excellence through empowerment.',
      image: 'https://images.unsplash.com/photo-1625244724120-1fd1d34d00f6',
      icon: Users
    }
  ];

  const founders = siteConfig.team;

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1561501900-3701fa6a0864)',
            transform: `translateY(${scrollY * 0.5}px)`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>
        
        <div className="relative h-full flex flex-col items-center justify-center text-white px-8 text-center">
          <h1 className="font-playfair text-6xl md:text-8xl font-bold mb-8 tracking-wider animate-fade-in">
            We are Repose Hotels
          </h1>
          <p className="text-xl md:text-3xl font-light tracking-widest mb-4 max-w-4xl animate-fade-in-delay">
            Strategic Hotel Asset Partners
          </p>
          <p className="text-lg md:text-xl font-light tracking-wide mb-12 max-w-3xl animate-fade-in-delay-2">
            Driving Hotel Performance Through Strategy, Discipline & Proven Leadership
          </p>
          <div className="flex gap-6 animate-fade-in-delay-3">
            <Button 
              size="lg" 
              className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
              asChild
            >
              <Link to="/contact">Partner With Us <ArrowRight className="ml-2" /></Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-[#2B2B2B] px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
              asChild
            >
              <Link to="/about">Learn More</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-32 px-8 bg-[#F4F4F4]">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-12 leading-relaxed tracking-wide">
            We believe hotels at their very core are spaces where stories intertwine, where dreams are nurtured, and where connections are forged.
          </h2>
          <p className="text-xl text-[#3A2E24] leading-relaxed max-w-4xl mx-auto">
            It is within these walls that we can truly harness our potential to impact the lives of our three key stakeholders: Owners, Guests, and Team. Through disciplined financial strategy and genuine guest-focused service culture, we set new standards in hospitality asset performance.
          </p>
        </div>
      </section>

      {/* Stakeholders Section */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {stakeholders.map((stakeholder, index) => {
              const Icon = stakeholder.icon;
              return (
                <Card 
                  key={index}
                  className="group relative overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105"
                >
                  <div className="relative h-80 overflow-hidden">
                    <img 
                      src={stakeholder.image} 
                      alt={stakeholder.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute bottom-8 left-8">
                      <Icon className="w-12 h-12 text-[#B89C4A] mb-4" />
                      <h3 className="font-playfair text-3xl font-bold text-white">{stakeholder.title}</h3>
                    </div>
                  </div>
                  <div className="p-8">
                    <p className="text-[#3A2E24] leading-relaxed">{stakeholder.description}</p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hotel Asset Management Section */}
      <section className="py-32 px-8 bg-[#F4F4F4]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative h-[600px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://images.pexels.com/photos/32978233/pexels-photo-32978233.jpeg" 
                alt="Hotel Management"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="font-playfair text-5xl font-bold text-[#2B2B2B] mb-8 tracking-wide">
                Strategic Hotel Asset Management
              </h2>
              <p className="text-xl text-[#3A2E24] leading-relaxed mb-6">
                We specialize in hotel acquisition, turnaround, and performance optimization. From revenue management to cost control and human resources, all facets of hotel operations are included in our comprehensive management services.
              </p>
              <p className="text-xl text-[#3A2E24] leading-relaxed mb-8">
                Our expertise spans franchise operations under major systems including Quest and Choice Hotels, ensuring strict compliance while maximizing profitability. We operate as if we own it—because we understand ownership risk.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 text-[#B89C4A] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#2B2B2B] mb-1">Award-Winning</h4>
                    <p className="text-sm text-[#3A2E24]">3× Franchise of the Year nominations</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-6 h-6 text-[#B89C4A] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#2B2B2B] mb-1">Proven Results</h4>
                    <p className="text-sm text-[#3A2E24]">Profitability achieved in 14 months</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Target className="w-6 h-6 text-[#B89C4A] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#2B2B2B] mb-1">#1 Rankings</h4>
                    <p className="text-sm text-[#3A2E24]">5-star TripAdvisor ratings achieved</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-6 h-6 text-[#B89C4A] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-[#2B2B2B] mb-1">Deep Experience</h4>
                    <p className="text-sm text-[#3A2E24]">40+ years combined expertise</p>
                  </div>
                </div>
              </div>
              <Button 
                size="lg" 
                className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white px-8 transition-all duration-300 hover:scale-105"
                asChild
              >
                <Link to="/services">Our Services <ArrowRight className="ml-2" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Hotels We Manage Section */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="font-playfair text-5xl font-bold text-[#2B2B2B] mb-8 tracking-wide">
                Proven Track Record
              </h2>
              <div className="space-y-8">
                <div>
                  <h3 className="font-playfair text-2xl font-semibold text-[#B89C4A] mb-4">Regional Victoria (2006)</h3>
                  <p className="text-lg text-[#3A2E24] leading-relaxed">
                    Acquired a struggling 60-room regional hotel with conference facilities, restaurant, and café operations. Through structured operational controls, sales strategy, and cost discipline, the property was transformed into a profitable and highly rated operation within 14 months.
                  </p>
                </div>
                <div>
                  <h3 className="font-playfair text-2xl font-semibold text-[#B89C4A] mb-4">Melbourne Apartment Hotel (2018)</h3>
                  <p className="text-lg text-[#3A2E24] leading-relaxed">
                    Acquired a 78-apartment hotel in Maribyrnong, entering an untested and challenging market. Through strategic positioning, corporate sales focus, and operational precision, the property achieved 5-star TripAdvisor rating, #1 ranking in its local market, and 3× Franchise of the Year nominations.
                  </p>
                </div>
              </div>
              <Button 
                size="lg" 
                className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white px-8 mt-8 transition-all duration-300 hover:scale-105"
                asChild
              >
                <Link to="/case-studies">View Case Studies <ArrowRight className="ml-2" /></Link>
              </Button>
            </div>
            <div className="relative h-[600px] rounded-lg overflow-hidden shadow-2xl order-1 lg:order-2">
              <img 
                src="https://images.unsplash.com/photo-1668480441891-3744c25337a3" 
                alt="Hotel Property"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-32 px-8 bg-[#2B2B2B] text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-playfair text-5xl font-bold text-center mb-6 tracking-wide">Our Leadership</h2>
          <p className="text-xl text-center text-gray-300 mb-12 max-w-3xl mx-auto">
            Between them, our directors have an unmatched combination of experience in the hospitality industry across Australia.
          </p>
          
          {/* Combined Photo */}
          <div className="max-w-2xl mx-auto mb-16">
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img 
                src={founders[0].image}
                alt="Scott Orchard and Anand Sangwan"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Individual Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {founders.map((founder, index) => (
              <Card key={index} className="bg-white/5 border-[#B89C4A]/20 backdrop-blur-sm p-8 hover:bg-white/10 transition-all duration-500">
                <h3 className="font-playfair text-3xl font-bold text-[#B89C4A] mb-2">{founder.name}</h3>
                <p className="text-lg text-gray-300 mb-4">{founder.title}</p>
                <p className="text-gray-200 leading-relaxed mb-6">{founder.description}</p>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[#B89C4A] mb-3">Key Expertise:</p>
                  {founder.expertise.map((skill, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-[#B89C4A] rounded-full" />
                      <span className="text-sm text-gray-300">{skill}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-16">
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-[#B89C4A] text-[#B89C4A] hover:bg-[#B89C4A] hover:text-white px-8 transition-all duration-300 hover:scale-105"
              asChild
            >
              <Link to="/about">Learn More About Our Team <ArrowRight className="ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Core Principles Section */}
      <section className="py-32 px-8 bg-[#F4F4F4]">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-16 tracking-wide">
            Our Commitment
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {[
              { title: '5-Star Service', description: 'Operational excellence is non-negotiable' },
              { title: 'Value for Money', description: 'Strong cost control without compromising guest experience' },
              { title: 'Presence', description: 'Active leadership involvement—not remote oversight' },
              { title: 'Flexibility', description: 'Adaptability to market cycles and demand shifts' },
              { title: 'Consistency', description: 'Reliable performance standards across all assets' }
            ].map((principle, index) => (
              <div key={index} className="group">
                <div className="bg-white p-8 rounded-lg shadow-lg h-full hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="w-16 h-16 bg-[#B89C4A] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white text-2xl font-bold">{index + 1}</span>
                  </div>
                  <h3 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-3">{principle.title}</h3>
                  <p className="text-[#3A2E24] text-sm leading-relaxed">{principle.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Quote Section */}
      <section className="relative py-32 px-8 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1621293954908-907159247fc8)'
          }}
        >
          <div className="absolute inset-0 bg-[#2B2B2B]/85" />
        </div>
        
        <div className="relative max-w-5xl mx-auto text-center">
          <blockquote className="font-playfair text-3xl md:text-4xl font-light text-white leading-relaxed mb-8">
            "We are unwavering in our commitment to exceed expectations, create memorable moments, build lasting relationships, and transform lives."
          </blockquote>
          <p className="text-xl text-[#B89C4A] font-semibold tracking-wider">— Repose Hotels Team</p>
        </div>
      </section>

      {/* Business Acquisition Assistance */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
              {siteConfig.acquisitionAssistance.title}
            </h2>
            <p className="text-xl text-[#3A2E24] max-w-3xl mx-auto">
              {siteConfig.acquisitionAssistance.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {siteConfig.acquisitionAssistance.services.map((service, index) => {
              const icons = [Briefcase, FileText, Growth, CheckSquare];
              const Icon = icons[index];
              return (
                <Card key={index} className="p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-16 h-16 bg-[#B89C4A] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-2">
                        {service.title}
                      </h3>
                      <p className="text-[#3A2E24] mb-4">{service.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-[#B89C4A] rounded-full mt-2 flex-shrink-0" />
                        <span className="text-sm text-[#3A2E24]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>

          <div className="bg-[#F4F4F4] p-8 rounded-lg">
            <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-6 text-center">
              {siteConfig.acquisitionAssistance.results.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {siteConfig.acquisitionAssistance.results.achievements.map((achievement, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-white p-4 rounded-lg">
                  <div className="w-8 h-8 bg-[#B89C4A] rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[#3A2E24] font-medium">{achievement}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-5xl font-bold text-[#2B2B2B] mb-8 tracking-wide">
            We welcome the opportunity to speak further.
          </h2>
          <p className="text-xl text-[#3A2E24] leading-relaxed mb-12">
            Contact us to see how our operating expertise can help drive sustainable improvement in your business.
          </p>
          <Button 
            size="lg" 
            className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white px-12 py-6 text-xl transition-all duration-300 hover:scale-105"
            asChild
          >
            <Link to="/contact">Speak With Us <ArrowRight className="ml-2" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
